import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Bot, HelpCircle, Maximize2, Printer, Search, X } from 'lucide-react';
import ModuleAccessContext from '../../auth/ModuleAccessContext';
import { kpToast } from '../ui/ToastProvider';
import DokumentVisare from '../../modules/Instruktioner/components/DokumentVisare';
import KanAIPanel from '../../modules/Instruktioner/components/KanAIPanel';
import KapitelLista from '../../modules/Instruktioner/components/KapitelLista';
import { useInstruktioner } from '../../modules/Instruktioner/hooks/useInstruktioner';
import { formateraDatum, gallerText } from '../../modules/Instruktioner/utils/format';
import { markeraLast } from '../../modules/Instruktioner/utils/lasstatus';
import { skrivUt } from '../../modules/Instruktioner/utils/skrivUt';
import Beskrivning, { rubriker } from './Beskrivning';
import ModulValjare from './ModulValjare';
import { filtreraPaBehorighet, valjStart } from './hjalpKontext';
import './hjalp.css';

const SENAST = 'kp-hjalp-senast';
const KANAI = 'kp-hjalp-kanai';

function lasLokalt(nyckel, reserv) {
  try { const v = window.localStorage.getItem(nyckel); return v === null ? reserv : JSON.parse(v); } catch { return reserv; }
}
function sparaLokalt(nyckel, varde) {
  try { window.localStorage.setItem(nyckel, JSON.stringify(varde)); } catch { /* privat läge */ }
}

/**
 * Hjälpvyn (?-knappen). Visar modulens instruktion med kapitel, sök och KanAI;
 * moduler utan instruktion visar den korta modulbeskrivningen.
 * Spec: docs/visions/instruktioner/hjalpvy-komponentspec.md.
 */
export default function HjalpVy({ open, onClose, currentPath, currentHash }) {
  const navigate = useNavigate();
  const { modules: accessSet, isAdmin } = useContext(ModuleAccessContext);
  const { poster } = useInstruktioner();
  const [allaBeskrivningar, setAllaBeskrivningar] = useState([]);
  const [version, setVersion] = useState(null);
  const [aktuell, setAktuell] = useState(null);
  const [flik, setFlik] = useState('instruktion');
  const [sida, setSida] = useState(1);
  const [sok, setSok] = useState('');
  const [traffar, setTraffar] = useState(null);
  const [visaAi, setVisaAi] = useState(() => lasLokalt(KANAI, true));
  const [beskrivningText, setBeskrivningText] = useState('');
  const anvandarValRef = useRef(false);
  const visareRef = useRef(null);
  const modalRef = useRef(null);
  const tidigareFokusRef = useRef(null);

  const instruktioner = useMemo(() => poster.filter((p) => p.publicerad && !p.variant_av), [poster]);
  const beskrivningar = useMemo(() => {
    const tackta = new Set(instruktioner.map((i) => i.hjalpnyckel).filter(Boolean));
    return filtreraPaBehorighet(allaBeskrivningar, accessSet, isAdmin).filter((b) => !tackta.has(b.key));
  }, [allaBeskrivningar, instruktioner, accessSet, isAdmin]);

  // Modulbeskrivningarna och versionen hämtas när hjälpen öppnas.
  useEffect(() => {
    if (!open) return;
    fetch(`/help/modules.json?_=${Date.now()}`).then((r) => r.json()).then(setAllaBeskrivningar).catch(() => {});
    fetch(`/version.json?_=${Date.now()}`).then((r) => (r.ok ? r.json() : null)).then(setVersion).catch(() => {});
  }, [open]);

  // Varje gång hjälpen öppnas börjar den i sidans modul.
  useEffect(() => {
    if (!open) return;
    anvandarValRef.current = false;
    tidigareFokusRef.current = document.activeElement;
    setTimeout(() => modalRef.current && modalRef.current.focus(), 0);
    return () => { if (tidigareFokusRef.current && tidigareFokusRef.current.focus) tidigareFokusRef.current.focus(); };
  }, [open]);

  const sidansVal = useMemo(() => valjStart({
    instruktioner, beskrivningar, vag: currentPath, hash: currentHash, senast: lasLokalt(SENAST, null),
  }), [instruktioner, beskrivningar, currentPath, currentHash]);

  useEffect(() => {
    if (!open || anvandarValRef.current) return;
    setAktuell(sidansVal);
  }, [open, sidansVal]);

  const instr = aktuell && aktuell.typ === 'instruktion' ? instruktioner.find((i) => i.nyckel === aktuell.nyckel) : null;
  const beskr = aktuell && aktuell.typ === 'beskrivning' ? beskrivningar.find((b) => b.key === aktuell.nyckel) : null;

  useEffect(() => {
    setFlik('instruktion'); setSida(1); setSok(''); setTraffar(null); setBeskrivningText('');
    if (aktuell) sparaLokalt(SENAST, aktuell);
  }, [aktuell]);
  useEffect(() => { if (open && instr) markeraLast(instr); }, [open, instr]);

  const valj = (val) => { anvandarValRef.current = true; setAktuell(val); };
  const satAi = (v) => { setVisaAi(v); sparaLokalt(KANAI, v); };

  const stang = useCallback(() => { if (onClose) onClose(); }, [onClose]);
  useEffect(() => {
    if (!open) return undefined;
    const tangent = (e) => {
      if (e.key === 'Escape') {
        if (document.querySelector('.hjalp-valjare.oppen')) return; // väljaren stänger sig själv först
        e.stopPropagation(); stang();
      } else if (e.key === 'Tab' && modalRef.current) {
        const fokus = modalRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea, a[href], [tabindex="0"]');
        if (!fokus.length) return;
        const forsta = fokus[0]; const sista = fokus[fokus.length - 1];
        if (e.shiftKey && document.activeElement === forsta) { e.preventDefault(); sista.focus(); }
        else if (!e.shiftKey && document.activeElement === sista) { e.preventDefault(); forsta.focus(); }
      }
    };
    document.addEventListener('keydown', tangent, true);
    return () => document.removeEventListener('keydown', tangent, true);
  }, [open, stang]);

  if (!open) return null;

  const titel = instr ? instr.titel : beskr ? beskr.title : 'Hjälp';
  const underrad = instr
    ? `Instruktion · uppdaterad ${formateraDatum(instr.datum)} · ${instr.sidor} sidor`
    : beskr ? 'Kort modulbeskrivning · instruktion saknas än' : '';
  const sokI = (v) => {
    setSok(v);
    if (visareRef.current) setTraffar(v.trim().length >= 2 ? visareRef.current.sok(v) : null);
    else if (v.trim().length >= 2) {
      const el = document.getElementById(rubriker(beskrivningText).find((r) => r.rubrik.toLowerCase().includes(v.trim().toLowerCase()))?.id || '');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTraffar(null);
    }
  };
  const tillKalla = (k) => {
    if (instr && k.nyckel === instr.nyckel) { setFlik('instruktion'); setTimeout(() => visareRef.current && k.sida && visareRef.current.gaTillSida(k.sida), 60); return; }
    if (k.nyckel) valj({ typ: 'instruktion', nyckel: k.nyckel });
  };
  const forslag = instr
    ? ['Vad är nytt i senaste versionen?', `Sammanfatta ”${(instr.kapitel[3] || instr.kapitel[0] || {}).rubrik || instr.titel}”`, 'Vad behöver jag göra innan jag börjar?']
    : [`Vad kan jag göra i ${titel}?`, 'Var hittar jag modulen i menyn?'];
  const kapitel = instr ? instr.kapitel : rubriker(beskrivningText).map((r) => ({ rubrik: r.rubrik, sida: null, id: r.id }));

  return (
    <div className="hjalp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) stang(); }}>
      <div className="hjalp-modal" role="dialog" aria-modal="true" aria-labelledby="hjalp-titel" ref={modalRef} tabIndex={-1}>
        <div className="hjalp-huvud">
          <div className="hjalp-titelblock">
            <span className="hjalp-ikon"><HelpCircle size={20} strokeWidth={2} aria-hidden="true" /></span>
            <div className="hjalp-titeltext">
              <h2 id="hjalp-titel">Hjälp — {titel}</h2>
              {underrad && <div className="hjalp-underrad">{underrad}</div>}
            </div>
          </div>
          <div className="hjalp-hoger">
            <ModulValjare instruktioner={instruktioner} beskrivningar={beskrivningar} aktuell={aktuell} sidansVal={sidansVal} titel={titel} onValj={valj} />
            <button type="button" className="instr-btn instr-btn--outline instr-btn--modal" onClick={() => { stang(); navigate('/instruktioner'); }}>
              <BookOpen size={15} strokeWidth={2.5} aria-hidden="true" />Alla instruktioner
            </button>
            <button type="button" className="instr-ikonknapp" onClick={stang} aria-label="Stäng hjälpen"><X size={18} strokeWidth={2.5} /></button>
          </div>
        </div>
        <div className="hjalp-flikar" role="tablist">
          {instr ? (
            <>
              <button type="button" role="tab" aria-selected={flik === 'instruktion'} className={flik === 'instruktion' ? 'aktiv' : ''} onClick={() => setFlik('instruktion')}>Instruktion</button>
              <button type="button" role="tab" aria-selected={flik === 'andringar'} className={flik === 'andringar' ? 'aktiv' : ''} onClick={() => setFlik('andringar')}>Ändringar</button>
            </>
          ) : <button type="button" role="tab" aria-selected className="aktiv">Modulbeskrivning</button>}
        </div>
        <div className={`hjalp-kropp${visaAi ? '' : ' utan-ai'}`}>
          <div className="hjalp-sida">
            <label className="hjalp-sok">
              <Search size={14} strokeWidth={2} aria-hidden="true" />
              <input value={sok} onChange={(e) => sokI(e.target.value)} disabled={flik === 'andringar'}
                onKeyDown={(e) => { if (e.key === 'Enter' && visareRef.current) { e.preventDefault(); visareRef.current.nastaTraff(); } }}
                placeholder={instr ? 'Sök i instruktionen' : 'Sök i beskrivningen'} aria-label="Sök" />
            </label>
            <div className="hjalp-traffar" aria-live="polite">{traffar === null ? '' : traffar ? `${traffar} träffar · Enter för nästa` : 'Inga träffar'}</div>
            <div className="hjalp-kapitel">
              <KapitelLista
                kapitel={kapitel}
                sida={sida}
                rubrik={instr ? 'Kapitel' : 'Innehåll'}
                visaSidnummer={!!instr}
                onValj={(s, k) => {
                  if (!instr) { const el = k && k.id && document.getElementById(k.id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
                  if (!s) return;
                  if (flik !== 'instruktion') setFlik('instruktion');
                  setTimeout(() => visareRef.current && visareRef.current.gaTillSida(s), 60);
                }}
              />
            </div>
            {instr && (
              <div className="hjalp-senast"><b>Senast ändrat</b>{formateraDatum(instr.datum)}{instr.notering ? ` — ${instr.notering}` : ''}</div>
            )}
          </div>
          <div className="hjalp-mitt">
            {instr && flik === 'instruktion' && (
              <DokumentVisare key={instr.nyckel} ref={visareRef} nyckel={instr.nyckel} sidor={instr.sidor} titel={instr.titel}
                visaSok={false} tips="Instruktionen visas som den skrivs ut. Bläddra, eller välj kapitel till vänster." onSida={setSida} />
            )}
            {instr && flik === 'andringar' && (
              <div className="hjalp-andringar">
                <div className="instr-sektion-rubrik">Ändringar i {instr.titel} <span>{instr.andringar.length}</span></div>
                {instr.andringar.map((a, i) => (
                  <button key={`${a.datum}-${i}`} type="button" className="hjalp-andring" disabled={!a.kapitel}
                    onClick={() => { const k = instr.kapitel[a.kapitel - 1]; setFlik('instruktion'); if (k && k.sida) setTimeout(() => visareRef.current && visareRef.current.gaTillSida(k.sida), 300); }}>
                    <span className={`datum${i === 0 ? ' senaste' : ''}`}>{formateraDatum(a.datum)}</span>
                    <span className="text">{a.text}{a.kapitel && instr.kapitel[a.kapitel - 1] ? <small>Kapitel {a.kapitel} · {instr.kapitel[a.kapitel - 1].rubrik}</small> : null}</span>
                  </button>
                ))}
              </div>
            )}
            {beskr && <Beskrivning modul={beskr} onText={setBeskrivningText} />}
            {!instr && !beskr && <div className="hjalp-tom">Ingen hjälp hittades för den här sidan. Välj en modul uppe till höger.</div>}
          </div>
          {visaAi && (
            <div className="hjalp-ai">
              <KanAIPanel
                nyckel={instr ? instr.nyckel : null}
                beskrivning={beskr ? { titel: beskr.title, text: beskrivningText } : null}
                underrad="AI-assistent för hela KanPlan"
                valkomst={`KanAI söker i alla instruktioner du har behörighet till, och börjar i ${titel}. Svaren visar vilket kapitel de bygger på och bör alltid granskas.`}
                forslag={forslag}
                kontext={instr ? `Börjar i: Instruktion ${instr.titel}, ${formateraDatum(instr.datum)}` : `Börjar i: Modulbeskrivning ${titel}`}
                onKalla={tillKalla}
                onStang={() => satAi(false)}
              />
            </div>
          )}
        </div>
        <div className="hjalp-fot">
          <div className="hjalp-fot-text">
            {instr ? `Gäller: ${gallerText(instr.galler)}` : 'Den här texten ersätts av en instruktion när modulen får en'}
            {version && version.app && version.app.version ? ` · KanPlan v${version.app.version}` : ''}
            {!visaAi && (
              <button type="button" className="hjalp-visa-ai" onClick={() => satAi(true)}>
                <Bot size={14} strokeWidth={2} aria-hidden="true" />Visa KanAI
              </button>
            )}
          </div>
          <div className="hjalp-fot-knappar">
            {instr ? (
              <>
                <button type="button" className="instr-btn instr-btn--outline instr-btn--modal"
                  onClick={() => skrivUt(instr.nyckel).catch(() => kpToast.error('Utskriften kunde inte startas'))}>
                  <Printer size={15} strokeWidth={2.5} aria-hidden="true" />Skriv ut / PDF
                </button>
                <button type="button" className="instr-btn instr-btn--primar instr-btn--modal"
                  onClick={() => { stang(); navigate(`/instruktioner/${instr.nyckel}${sida > 1 ? `?sida=${sida}` : ''}`); }}>
                  <Maximize2 size={15} strokeWidth={2.5} aria-hidden="true" />Öppna i läsläge
                </button>
              </>
            ) : (
              <button type="button" className="instr-btn instr-btn--primar instr-btn--modal" onClick={stang}>Stäng</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
